<template>
  <!-- Loading -->
  <mc-loading-indicator
    v-if="showSpinner"
    class="card card--loading"
    variant="spinner"
    appearance="primary"
    fit="large"
    :label="$t('common.loading')"
  />

  <div v-else data-test="documents" class="documents">
    <div class="mb-4">{{ $t("documents.description") }}</div>

    <!-- Filters -->
    <div data-test="document-tab-filters" class="table-filters">
      <div class="table-filters__wrapper">
        <mc-select
          :label="`${$t('common.departingIn')}:`"
          name="timeFrames"
          :value="timeFrameValue"
          labelposition="left"
          @optionselected="updateTimeFrame"
        >
          <mc-option v-for="opt in timeframeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</mc-option>
        </mc-select>
        <mc-select
          :label="`${$t('documents.type')}:`"
          name="documentType"
          labelposition="left"
          :value="selectedDocumentType"
          @optionselected="updateDocumentType"
        >
          <mc-option v-for="opt in documentTypes" :key="opt.value" :value="opt.value">{{ opt.label }}</mc-option>
        </mc-select>
      </div>
    </div>

    <!-- No results -->
    <MarketingNotification
      v-if="documentsOnPage && documentsOnPage.length === 0 && !shipmentsError && !showSpinner"
      class="card"
    />

    <!-- Error -->
    <mc-notification
      v-else-if="documentsOnPage && documentsOnPage.length === 0 && !showSpinner"
      width="fit-content"
      class="card"
      appearance="error"
      icon="exclamation-circle"
      :body="$t('datatable.error.noRecords500')"
    />

    <!-- Results -->
    <mc-table
      v-else
      fit="small"
      sortdisabled
      :data.prop="documentsOnPage"
      :columns.prop="[
        {
          id: 'blNumber',
          label: $t('common.blNumber'),
          subDataLabel: $t('tables.references'),
          verticalAlign: 'top',
          width: '25%'
        },
        {
          id: 'from',
          label: $t('common.from'),
          subDataLabel: $t('tables.depPort'),
          verticalAlign: 'top'
        },
        {
          id: 'to',
          label: $t('common.to'),
          subDataLabel: $t('tables.arrPort'),
          verticalAlign: 'top'
        },
        {
          id: 'documents',
          label: $t('documents.documentsLabel'),
          verticalAlign: 'top'
        }
      ]"
      data-test="documents-table"
    >
      <template v-for="item in documentsOnPage" :key="item.id">
        <div :slot="`${item.id}_blNumber`">
          <a
            data-test="document-table-shipment-link"
            :href="`/shipment-details/${item.ladingNumber}`"
            @click="trackShipmentClick('documents', $route?.path?.toLowerCase()?.includes?.('export'))"
          >
            <b>{{ item.ladingNumber }}</b>
          </a>
          <p v-if="item.bookedByRef" class="color-neutral-subtle mds-text--small-normal wrap-anywhere">
            {{ $t("common.bookedByRef") }} ,
            {{ item.bookedByRef }}
          </p>
        </div>
        <div :slot="`${item.id}_from`">
          <FromTo :place="item?.placeOfReceipt || item?.portOfLoading" :port="item?.portOfLoading" />
        </div>
        <div :slot="`${item.id}_to`">
          <FromTo
            :place="item?.placeOfDelivery || item?.portOfDischarge"
            :port="item?.portOfDischarge"
            type="arrival"
          />
        </div>
        <div :slot="`${item.id}_documents`">
          <DocumentDownload
            :documentType="item.documentType"
            :parties="item.parties"
            :shipmentNumber="item.shipmentNumber"
          />
        </div>
      </template>
    </mc-table>

    <Pagination
      v-if="documentsOnPage && documentsOnPage.length > 0 && lastPage > 1"
      activeView="documents"
      :lastPage="lastPage"
      :totalCount="totalCount"
      :currentPage="paginationToken"
      @update-data-source="getData"
    />
  </div>
</template>
<script>
import Pagination from "@/components/shared/Pagination.vue";
import MarketingNotification from "@/components/shared/MarketingNotification.vue";
import DocumentDownload from "@/components/shared/DocumentDownload.vue";
import FromTo from "@/components/shared/FromTo.vue";
import { mapActions, mapState, mapWritableState } from "pinia";
import { documents } from "@/store/documents";
import { filters } from "@/store/filters";
import { getOptionsFromLocalStorage, trackShipmentClick } from "@/lib/utilities";

export default {
  name: "DocumentsTable",
  components: { MarketingNotification, Pagination, DocumentDownload, FromTo },
  data() {
    return {
      documentTypeValue: "all",
      timeframeOptions: [
        { label: this.$t("timings.next3days"), value: "next_3_days" },
        { label: this.$t("timings.next7days"), value: "next_7_days" },
        { label: this.$t("timings.nextMonth"), value: "next_month" },
        { label: this.$t("timings.next3months"), value: "next_3_months" },
        { label: this.$t("timings.last3days"), value: "last_3_days" },
        { label: this.$t("timings.last7days"), value: "last_7_days" },
        { label: this.$t("timings.lastMonth"), value: "last_month" },
        { label: this.$t("timings.last3months"), value: "last_3_months" }
      ],
      documentTypes: [
        { label: this.$t("documents.all"), value: "all" },
        { label: this.$t("documents.verifyCopy"), value: "verifyCopy" },
        { label: this.$t("documents.billOfLading"), value: "billOfLading" },
        { label: this.$t("documents.waybill"), value: "waybill" }
      ]
    };
  },
  computed: {
    ...mapState(documents, [
      "documentsOnPage",
      "shipmentsError",
      "showSpinner",
      "paginationToken",
      "lastPage",
      "totalCount",
      "selectedDocumentType",
      "timeFrameValue"
    ]),
    ...mapWritableState(documents, ["selectedDocumentType", "timeFrameValue"]),
    ...mapState(filters, ["activeFilter"])
  },
  watch: {
    activeFilter: {
      async handler() {
        await this.updatePaginationToken(1);
        await this.getDocuments(this.timeFrameValue);
      },
      deep: true
    }
  },
  async mounted() {
    this.checkTimeFrameFromLocalStorage();
    this.setDefaultSortValues();
    if (this.documentsOnPage.length === 0) {
      await this.getPermissions();
      await this.getDocuments(this.timeFrameValue);
      this.$sendGAEvent({
        event: "UXEvent",
        action: "Vessel departing QF",
        category: "Documents",
        label: this.timeframeOptions.find((time) => this.timeFrameValue === time.value)?.label
      });
    }
  },

  methods: {
    ...mapActions(documents, [
      "getDocuments",
      "filterDocuments",
      "updateSelectedDocumentType",
      "updatePaginationToken",
      "getPermissions",
      "checkTimeFrameFromLocalStorage"
    ]),
    trackShipmentClick,
    setDefaultSortValues() {
      this.selectedDocumentType =
        getOptionsFromLocalStorage(["all", "verifyCopy", "billOfLading", "waybill"], "documentTypeFilterSO") || "all";
    },
    async updateTimeFrame($event) {
      this.timeFrameValue = $event.target.value;
      this.updatePaginationToken(1);
      localStorage.setItem("documentsTimeFrameSO", $event.target.value);
      await this.getDocuments($event.target.value);
      localStorage.setItem("timeFrameSO", this.timeFrame);
      this.$sendGAEvent({
        event: "UXEvent",
        action: "Vessel departing QF",
        category: "Documents",
        label: this.timeframeOptions.find((time) => this.timeFrameValue === time.value)?.label
      });
    },
    updateDocumentType($event) {
      this.updateSelectedDocumentType($event.target.value);
      this.$sendGAEvent({
        event: "UXEvent",
        action: "Document type QF",
        category: "Documents",
        label: this.documentTypes.find((item) => this.documentTypeValue === item.value)?.label
      });
    },
    getData(event) {
      this.updatePaginationToken(event);
    }
  }
};
</script>
